import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import './Gallery.css';
import './Detail.css';
import { getImageUrl, getRemasteredImageUrl, getPixelatedImageUrl } from '../utils/helpers';
import { getThingTraits } from '../utils/thingsData';
import traitColours from '../utils/trait_colours.json';

const HOVER_COLORS = ['#ffc964', '#d65952', '#7ec28a', '#6ba1cf', '#a089f1'];

const colourMap = {
  'yellow': '#ffb833', 'red': '#b52524', 'green': '#186070', 'blue': '#1b65cf',
  'purple': '#6a2c91', 'black': '#1a1a1a', 'white': '#e8e8e8', 'cream': '#f5e6c8',
  'gold': '#d4a017'
};

const ImageTypes = {
  original: 0,
  pixelated: 1,
  remastered: 2,
  // floaty: 3,
};

export default function ThingDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const currentId = parseInt(id);
  const [imageTypeId, setImageTypeId] = useState(ImageTypes.original);
  const [showMetadata, setShowMetadata] = useState(false);

  const imageUrl = useMemo(() => {
    switch (imageTypeId) {
      case ImageTypes.original:
        return getImageUrl(id);
      case ImageTypes.remastered:
        return getRemasteredImageUrl(id);
      case ImageTypes.pixelated:
        return getPixelatedImageUrl(id);
      default:
        return getImageUrl(id);
    }
  }, [id, imageTypeId]);

  // Traits are now fetched lazily from /things-data.json instead of statically
  // imported. The fetch happens once per session (cached at the module level)
  // and only when a Thing detail page is actually visited.
  const [traits, setTraits] = useState([]);
  useEffect(() => {
    let cancelled = false;
    getThingTraits(currentId)
      .then((t) => { if (!cancelled) setTraits(t); })
      .catch((err) => {
        console.warn('thing traits load failed', err);
        if (!cancelled) setTraits([]);
      });
    return () => { cancelled = true; };
  }, [currentId]);

  const orderedTraits = useMemo(() => {
    if (!traits.length) return [];
    const traitOrder = ['Background', 'Body', 'Body Detail', 'Eyes', 'Head', 'Headwear', 'Misc', 'Mouth'];
    return traits
      .filter(trait => trait.trait_type !== 'Traits Count')
      .sort((a, b) => traitOrder.indexOf(a.trait_type) - traitOrder.indexOf(b.trait_type));
  }, [traits]);

  const traitColourSwatches = useMemo(() => {
    if (!orderedTraits.length) return [];
    return orderedTraits
      .map(trait => {
        const key = `${trait.trait_type.toLowerCase()}:${trait.trait_value.toLowerCase()}`;
        const entry = traitColours[key];
        if (!entry || !entry.literal) return null;
        const literals = entry.literal.split(',').map(c => c.trim().toLowerCase());
        return {
          type: trait.trait_type.toLowerCase(),
          colours: literals.map(lit => ({
            name: lit,
            hex: colourMap[lit.split(' ').pop()] || '#ccc'
          }))
        };
      })
      .filter(Boolean);
  }, [orderedTraits]);

  // function handleBackButtonClick() {
  //   navigate('/');
  // }

  const handlePreviousClick = useCallback(() => {
    if (currentId > 1) {
      navigate(`/things/${currentId - 1}`);
    }
  }, [currentId, navigate]);

  const handleNextClick = useCallback(() => {
    if (currentId < 10000) {
      navigate(`/things/${currentId + 1}`);
    }
  }, [currentId, navigate]);

  function handleAlternateArtworkClick(selectedImageType) {
    setImageTypeId(selectedImageType);
    localStorage.setItem('thingDetailImageType', selectedImageType);
  }

  useEffect(() => {
    const savedImageTypeId = localStorage.getItem('thingDetailImageType');

    if (savedImageTypeId !== null) {
      setImageTypeId(JSON.parse(savedImageTypeId));
    } else if (!savedImageTypeId) {
      setImageTypeId(ImageTypes.original);
    }
  }, []);

  useEffect(() => {
    function handleKeyPress(event) {
      if (event.key === 'ArrowLeft') {
        handlePreviousClick();
      } else if (event.key === 'ArrowRight') {
        handleNextClick();
      }
    }

    window.addEventListener('keydown', handleKeyPress);

    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, [handlePreviousClick, handleNextClick]);

  return (
    <div>
      {/* <header style={{ height: '40px' }}>
        <div className="header-container">
          <div className="back-button">
            <img src={rightArrow} alt="Back Arrow" onClick={handleBackButtonClick} />
          </div>
        </div>
      </header> */}
      <div className="home-container">
        <div className="body-text">&nbsp;</div>
        <div className="main-image">
          <img 
            src={imageUrl} 
            alt={`Thing ${id}`} 
          />
        </div>
        <div className="caption-text" style={{ marginBottom: '28px' }}>
          Digital artwork ({imageTypeId === ImageTypes.pixelated ? 'GIF' : 'PNG'})
        </div>
        <ul className="alternate-artwork-list">
          <li><div className={`bold-text body-text ${imageTypeId === ImageTypes.original ? 'active' : ''}`} style={{ '--hover-color': HOVER_COLORS[0] }} onClick={() => handleAlternateArtworkClick(ImageTypes.original)}>Original version</div></li>
          <li><div className={`bold-text body-text ${imageTypeId === ImageTypes.remastered ? 'active' : ''}`} style={{ '--hover-color': HOVER_COLORS[1] }} onClick={() => handleAlternateArtworkClick(ImageTypes.remastered)}>Remastered version</div></li>
          <li><div className={`bold-text body-text ${imageTypeId === ImageTypes.pixelated ? 'active' : ''}`} style={{ '--hover-color': HOVER_COLORS[2] }} onClick={() => handleAlternateArtworkClick(ImageTypes.pixelated)}>Pixel version</div></li>
          {/* <li><div className={`bold-text body-text ${imageTypeId === ImageTypes.floaty ? 'active' : ''}`} onClick={() => handleAlternateArtworkClick(ImageTypes.floaty)}>Floaty version</div></li> */}
        </ul>
        <div
          className="bold-text body-text trait-toggle"
          onClick={() => setShowMetadata(!showMetadata)}
        >
          <span className="title-text title-link">
            {`Thing #${id}`.split('').map((letter, index) => (
              <span key={index} className="title-letter" style={{ '--letter-index': index }}>
                {letter === ' ' ? '\u00A0' : letter}
              </span>
            ))}
          </span>
        </div>
        {showMetadata && orderedTraits.length > 0 && (
          <div className="trait-data">
            <div className="trait-data-cell">
              <div className="trait-data-cell-label">Traits</div>
              <div className="trait-data-cell-value">
                {orderedTraits.map((trait, i) => (
                  <React.Fragment key={trait.trait_type}>
                    <Link
                      to={`/?trait_type=${encodeURIComponent(trait.trait_type)}&trait_value=${encodeURIComponent(trait.trait_value)}`}
                      className="trait-link"
                    >
                      {trait.trait_value.toLowerCase()}
                    </Link>
                    <span className="pool-tag">{trait.trait_type.toLowerCase()}</span>
                    {i < orderedTraits.length - 1 && ', '}
                  </React.Fragment>
                ))}
              </div>
            </div>
            {traitColourSwatches.length > 0 && (
              <div className="trait-data-cell">
                <div className="trait-data-cell-label">Colours</div>
                <div className="trait-data-cell-value">
                  {traitColourSwatches.map((tc, i) => (
                    <React.Fragment key={tc.type}>
                      {tc.colours.map((c, ci) => (
                        <span key={ci} className="csw" style={{ backgroundColor: c.hex }} />
                      ))}
                      <span className="pool-tag">{tc.type}</span>
                      {i < traitColourSwatches.length - 1 && ', '}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        <div className="arrow-container">
          <button
            onClick={handlePreviousClick}
            disabled={currentId <= 1}
            className="arrow-button"
            aria-label="Previous"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="19" y1="12" x2="5" y2="12"/>
              <polyline points="12 19 5 12 12 5"/>
            </svg>
          </button>
          <button
            onClick={handleNextClick}
            disabled={currentId >= 10000}
            className="arrow-button"
            aria-label="Next"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="5" y1="12" x2="19" y2="12"/>
              <polyline points="12 5 19 12 12 19"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};
